/**
 * The visitor's own position from the route card (#204, owner 08.10.2026: «Я как
 * юзер не буду в отладочное окно заходить за картой»).
 *
 * A route that failed for want of a start used to say «выберите точку в панели
 * «Местоположение»» — a panel behind «Отладка». The card now asks the browser
 * itself, BY A TAP (UI_CONTRACT §4.2: real geolocation or none), commits the
 * point exactly as the panel's «Использовать эту точку» does (`setAgentGeo`,
 * source `gps`) and sends the visitor's request again as an ordinary message —
 * the run then carries the new position in its context and `navigate_to` has
 * its start. Nothing here reads what the visitor wrote: the button exists
 * because the ROUTE says it has no origin, not because of a phrase.
 *
 * Every build since 12.10.2026, like everything on the card (`setAgentGeo` is a
 * no-op outside `peterhof_agent`).
 */

import type { GeoMeta } from '../store/sessionStore';
import type { NavigationPayload } from './navigation';
import { APPROXIMATE_M, approximateText } from './reverseGeocode';

/** The visitor's line the card sends once the position is known. */
export const ROUTE_FROM_HERE = 'Покажи маршрут на карте от моего места';

// Not only «у какого входа»: the visitor may be anywhere — an address builds the trip by transport (12.10.2026).
const NAME_IT = 'Напишите в чате, где вы: объект музея, вход или адрес.';

/** The card's own words for a route with no start — in place of the backend's
 *  reason, which points at the «Местоположение» panel of the debug drawer. */
export const NO_START_TEXT = `Чтобы построить маршрут, нужно знать, где вы. Нажмите «Определить моё место» или ${NAME_IT[0]!.toLowerCase()}${NAME_IT.slice(1)}`;

/** A failure the card can show as it is — or its own neutral line when the
 *  backend's reason was written for the MODEL (live run 08.10.2026: «…id 54 —
 *  западная точка… вызови navigate_to(scope=plan, …)» on the visitor's card).
 *  A belt only: the backend is fixed at the source; this never reads what the
 *  VISITOR wrote, only whether the backend's own text leaks tool talk. */
const MODEL_FACING = /вызови|navigate_to|plan_points|\bid\s*\d|служебн/i;
export const NEUTRAL_ERROR = 'Маршрут пока не построен — гид уточнит детали в чате.';
export function visitorError(error: string | undefined): string {
  if (!error) return 'Не удалось построить маршрут. Попробуйте ещё раз.';
  return MODEL_FACING.test(error) ? NEUTRAL_ERROR : error;
}

/** A museum route the card could build had it a start: failed, no origin. An
 *  arrival has its own way — an address typed in the chat. */
export function needsOrigin(preview: NavigationPayload | null): boolean {
  return !!preview && preview.status === 'error' && !preview.origin && preview.scope !== 'arrival';
}

/** A «что рядом» card with no start (#215) — the same button, its own words. */
export const PLACES_NO_START_TEXT = `Чтобы найти места рядом, нужно знать, где вы. Нажмите «Определить моё место» или напишите в чате, где вы находитесь.`;

/** The visitor's line a «что рядом» card sends once the position is known: the category the tool named. */
export function placesFromHere(category: string | undefined): string {
  const what = (category ?? '').replace(/[^\p{L}\p{N} -]/gu, '').trim().slice(0, 40);
  return what ? `Что рядом с моим местом: ${what}?` : 'Что рядом с моим местом?';
}

/** The «Где вы?» card (INV-PH-103, owner 15.10.2026): a tool needed the visitor's place — «что рядом со мной?», a trip
 *  with no start — and had none. The same buttons as a route with no start, without a route; the entrances are the
 *  buttons under the answer. */
export const WHERE_TEXT = 'Чтобы ответить, нужно знать, где вы. Нажмите «Определить моё место», «Указать адрес» или выберите вход под ответом.';

/** The same card for a trip to the museum (`purpose: 'arrival'`, v2): no entrances under the answer — the start is a
 *  place in the city (dev 22.10: «как дабратца» showed «выберите вход под ответом» over no entrance). */
export const WHERE_ARRIVAL_TEXT = 'Чтобы построить дорогу, нужно знать, откуда вы поедете. Нажмите «Определить моё место», «Указать адрес» или напишите в чате, откуда поедете.';

/** A «что успею за N минут» card with no start (#216) — the same button, its own words. */
export const REACH_NO_START_TEXT = `Чтобы посчитать, что успеете обойти, нужно знать, где вы. Нажмите «Определить моё место» или напишите в чате, где вы находитесь.`;

/** The visitor's line a «что успею» card sends once the position is known: the minutes the tool was given. */
export function reachFromHere(minutes: number | undefined): string {
  return Number.isInteger(minutes) && minutes! > 0 && minutes! <= 60
    ? `Что успею за ${minutes} минут пешком от моего места?`
    : 'Что успею обойти пешком от моего места?';
}

/** What the card says when the browser gives no position. */
export function locateFailureText(error: { code: number } | null): string {
  // GeolocationPositionError.PERMISSION_DENIED === 1
  return error?.code === 1
    ? `Доступ к геопозиции закрыт. ${NAME_IT}`
    : `Не получилось определить место. ${NAME_IT}`;
}

/** The card's state once the visitor turns to another way of naming their place («Указать адрес», «Указать на карте»):
 *  the line about the GPS that failed — «Доступ к геопозиции закрыт…» — and the coarse fix waiting for «Всё равно
 *  использовать» belong to the way they left (owner's QA 24.10.2026, B18). The name of a place already taken stays. */
export function leavingGps<T extends { failure: string | null; approx: unknown }>(state: T): T {
  return { ...state, failure: null, approx: null };
}

/** What `send` answers (`lib/send.ts`): `false` — the message was not taken (a turn is running, offline). */
type Sender = (text: string) => unknown;

type Deps = {
  geolocation: Pick<Geolocation, 'getCurrentPosition'> | undefined;
  setAgentGeo: (point: { lat: number; lon: number }, meta: Partial<GeoMeta>) => void;
  send: Sender;
  /** The line sent once the position is known — the route's by default. */
  text?: string;
  /** Puts the visitor's place back as it was before the commit — called when `send` did not take the message
   *  (a turn is already running: «ничего не менять», INV-PH-137). */
  rollback?: () => void;
  /** 2GIS Geocoder: the point's address, or `null` (never throws, never waits past its own timeout). */
  reverse?: (lat: number, lon: number) => Promise<string | null>;
  /** Called with the named place before it is committed — the card shows «Вы: …» (INV-PH-140). */
  onPlace?: (place: VisitorPlace) => void;
  /** Called INSTEAD of committing when the fix is coarser than `APPROXIMATE_M`: the card says so, offers the
   *  buttons and the address, and keeps the place for «Всё равно использовать». Without it a coarse fix is used. */
  onApproximate?: (place: VisitorPlace) => void;
};

/** A browser position, named when the geocoder could. */
export type VisitorPlace = { lat: number; lon: number; at: number; accuracyM: number; label: string | null };

/** Commit the place as the visitor's GPS and send the request. → whether the message was taken; when it was not,
 *  `rollback` has put the old place back and nothing changed. */
export function commitPlace(place: VisitorPlace, { setAgentGeo, send, text = ROUTE_FROM_HERE, rollback }: Pick<Deps, 'setAgentGeo' | 'send' | 'text' | 'rollback'>): boolean {
  setAgentGeo({ lat: place.lat, lon: place.lon }, { source: 'gps', at: place.at, accuracyM: place.accuracyM, label: place.label });
  if (send(text) === false) {
    rollback?.();
    return false;
  }
  return true;
}

/** What the card says when a request could not go out because a turn is running — the buttons are inactive then,
 *  this is the line for a press that slipped through. */
export const NOT_SENT_TEXT = 'Гид отвечает на предыдущее сообщение — подождите окончания ответа и нажмите ещё раз.';

/** Ask the browser once; on success name the place (2GIS Geocoder, a short wait), commit it and send the request.
 *  Resolves with `null` when sent, or with the text to show instead. */
export function locateAndAsk({ geolocation, setAgentGeo, send, text = ROUTE_FROM_HERE, rollback, reverse, onPlace, onApproximate }: Deps): Promise<string | null> {
  if (!geolocation) return Promise.resolve(locateFailureText(null));
  return new Promise((resolve) => {
    geolocation.getCurrentPosition(
      (position) => {
        const { latitude: lat, longitude: lon, accuracy } = position.coords;
        const named = reverse ? reverse(lat, lon).catch(() => null) : Promise.resolve(null);
        void named.then((label) => {
          const place: VisitorPlace = { lat, lon, at: position.timestamp, accuracyM: accuracy, label };
          if (onApproximate && accuracy > APPROXIMATE_M) {
            onApproximate(place);
            resolve(approximateText(accuracy));
            return;
          }
          onPlace?.(place);
          resolve(commitPlace(place, { setAgentGeo, send, text, rollback }) ? null : NOT_SENT_TEXT);
        });
      },
      (error) => resolve(locateFailureText(error)),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  });
}

/**
 * «Указать адрес» on the same card (#222, INV-PH-98): the 2GIS suggestion the visitor picked is committed
 * exactly as the «Местоположение» panel's address search commits it (`setAgentGeo`, source `manual`, the
 * place's name), and the same request goes out again as after GPS — the run carries the new position.
 * The departure buttons (INV-PH-140) are the same thing with a point of the data file. → whether the message was
 * taken; when not, `rollback` has put the old place back.
 */
export function pickAndAsk({ item, setAgentGeo, send, text = ROUTE_FROM_HERE, rollback }: Omit<Deps, 'geolocation'> & {
  item: { lat: number; lon: number; title: string };
}): boolean {
  setAgentGeo({ lat: item.lat, lon: item.lon }, { source: 'manual', at: Date.now(), accuracyM: null, label: item.title });
  if (send(text) === false) {
    rollback?.();
    return false;
  }
  return true;
}

/** A 2GIS walk from where the visitor IS to a point, for a card that has no start of its own (INV-PH-140): the
 *  start is left empty — `https://2gis.ru/spb/directions/tab/pedestrian/points/|<lon>,<lat>` opens the pedestrian tab
 *  with the destination set and offers «Моё местоположение» for the start (checked in a browser 18.10.2026). Not
 *  the `routeSearch` form: it opened Moscow's map on 25.09.2026. `null` for a point with no known coordinates. */
export function fromHereLink(point: { lat: number; lon: number; title: string } | null | undefined): { href: string; title: string } | null {
  if (!point || !Number.isFinite(point.lat) || !Number.isFinite(point.lon)) return null;
  return { href: `https://2gis.ru/spb/directions/tab/pedestrian/points/|${point.lon.toFixed(6)},${point.lat.toFixed(6)}`, title: point.title };
}

/** The card with no start that has ONE obvious place to walk to: a chain's first point, a destination with no
 *  start, a single «где …?» point the visitor has no start for. */
export function fromHereTarget(route: NavigationPayload): { lat: number; lon: number; title: string } | null {
  if (route.scope === 'arrival' || route.origin) return null;
  const goal = route.points?.[0] ?? route.destination;
  if (!goal) return null;
  if (route.points && route.points.length > 1 && !route.chain) return null; // several candidates: the visitor picks one first
  return goal;
}

/** A pedestrian route in 2GIS from the route's start to its NEXT stop — the
 *  first stop on the way, or the destination when there is none. The form was
 *  checked against 2gis.ru on 08.10.2026 (`/spb/directions/tab/pedestrian/points/
 *  <lon>,<lat>|<lon>,<lat>` answers with the pedestrian tab in Saint Petersburg).
 *  No key: a 2GIS key never goes into a URL a visitor can see. */
export function nextStopLink(route: NavigationPayload): { href: string; title: string } | null {
  if (route.status !== 'ready' || route.scope === 'arrival' || !route.origin || !route.destination) return null;
  const next = route.via?.[0] ?? route.destination;
  const point = (p: { lat: number; lon: number }) => `${p.lon.toFixed(6)},${p.lat.toFixed(6)}`;
  return {
    href: `https://2gis.ru/spb/directions/tab/pedestrian/points/${point(route.origin)}|${point(next)}`,
    title: next.title,
  };
}

/** The whole walk in 2GIS (INV-PH-101, owner 15.10.2026): the start, every stop in its order, the destination —
 *  beside the next-stop link, for a walk through stops. The same URL form as `nextStopLink`; 2GIS takes up to ten
 *  points between the ends, so a longer plan keeps only the next-stop link. */
export const MAX_WHOLE_ROUTE_VIA = 10;
export function wholeRouteLink(route: NavigationPayload): string | null {
  if (route.status !== 'ready' || route.scope === 'arrival' || !route.origin || !route.destination) return null;
  const via = route.via ?? [];
  if (via.length === 0 || via.length > MAX_WHOLE_ROUTE_VIA) return null;
  const point = (p: { lat: number; lon: number }) => `${p.lon.toFixed(6)},${p.lat.toFixed(6)}`;
  return `https://2gis.ru/spb/directions/tab/pedestrian/points/${[route.origin, ...via, route.destination].map(point).join('|')}`;
}

/** A chain of points with no start, as its title: «Большой каскад → Самсон → Марли» (INV-PH-101). */
export function chainTitle(points: NonNullable<NavigationPayload['points']>): string {
  return points.map((point) => point.title).join(' → ');
}

/** The card's words for a chain with no start (INV-PH-101): the start is chosen here — its first point by default. */
export function chainNoStartText(first: string): string {
  return `Откуда начать? «${startLabel(first)}» — путь по всей цепочке от первой точки. Или нажмите «Определить моё место», укажите адрес или выберите вход под ответом.`;
}

/** The card's button that starts a chain at its first point — the backend's `start_button`, word for word. */
export function startLabel(title: string): string {
  return `Начать с «${title}»`;
}

/** The button key of «Начать с «A»» (`graphs/peterhof_plan/starter.py`, `route_from:`): the chain's museum ids, the
 *  start first and the destination last — the graph makes the walk's call in code, no place asked. `null` past the
 *  walk's own bound (three stops between the ends). */
export function routeFromStarter(points: NonNullable<NavigationPayload['points']>): string | null {
  if (points.length < 2 || points.length > 5 || !points.every((point) => /^\d+$/.test(point.id))) return null;
  return `route_from:${points.map((point) => point.id).join(';')}`;
}
